import React, { useCallback, useEffect, useState } from "react";
import { getProducts, getProductsCategories } from "../../../../api/product";
import Grid from "@mui/material/Grid";
import ProductCard from "./ProductCard";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import SearchIcon from "@mui/icons-material/Search";
import { Grow, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import debounce from "lodash.debounce";
import {
  setCategoryFilter,
  setProductTitleFilter,
} from "../../../../store/actions/newOrderSlice";
import { StyledInputBase } from "../../../styled/inputs/searchInput";
import { Windows11CategoryButton } from "../../../styled/pos/windows11";

const Products = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const categoryFilter = useSelector((state) => state.newOrder.categoryFilter);
  const productTitleFilter = useSelector(
    (state) => state.newOrder.productTitleFilter
  );

  const expandGrids = useSelector((state) => state.newOrder.expandGrids);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data } = await getProductsCategories();
        const categoriesSorted = data.sort((a, b) =>
          a.title.localeCompare(b.title)
        );
        setCategories(categoriesSorted);
      } catch (e) {
        console.error(e);
      }
    };
    loadCategories();
  }, []);

  // Load products
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const params = {
          page: 1,
          category: categoryFilter,
          title: productTitleFilter,
          include_ifood: 1,
        };
        const { data } = await getProducts(params);

        let treatedProducts = data;
        if (categoryFilter) {
          treatedProducts = data.map((product) => {
            return {
              ...product,
              product_id: product.product,
              title: product.product_title,
              price: product.product_price,
              is_ifood_imported: product.is_ifood_imported,
              channel: product.channel,
            };
          });
        }

        setProducts(treatedProducts);
      } catch (e) {
        console.error(e);
      }
    };

    loadProducts();
  }, [categoryFilter, productTitleFilter]);

  // Create a debounced version of the dispatch function
  // This will only trigger after 300ms of no typing
  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(setProductTitleFilter(query));
    }, 300),
    [dispatch]
  );

  // Cleanup the debounced function on component unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handler for clearing the input
  const handleClear = () => {
    setSearchQuery("");
    dispatch(setProductTitleFilter(undefined)); // Clear the filter immediately
  };

  // Handler for input changes
  const handleProductsSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Handler for category clicks
  const handleCategoryClick = (categoryId) => {
    dispatch(setCategoryFilter(categoryId));
  };

  return (
    <Box>
      {/* Categories + Search Bar Row */}
      <Box
        sx={{
          my: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Categories - Horizontal Scroll */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflowX: "auto",
            flex: 1,
            minWidth: 0,
            pb: 0,
            // Custom scrollbar styling
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: grey[50],
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: grey[300],
              borderRadius: 3,
              "&:hover": {
                backgroundColor: grey[400],
              },
            },
          }}
        >
          {/* All Categories Button */}
          <Grow in={true} timeout={300}>
            <Box>
              <Windows11CategoryButton
                isselected={!categoryFilter ? 1 : 0}
                colorhex={grey[400]}
                onClick={() => handleCategoryClick(null)}
                sx={{
                  whiteSpace: "nowrap",
                  minHeight: 40,
                  px: 2,
                }}
              >
                <CategoryOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography
                  variant="body2"
                  fontWeight={!categoryFilter ? 600 : 500}
                >
                  Todas
                </Typography>
              </Windows11CategoryButton>
            </Box>
          </Grow>

          {/* Individual Categories */}
          {categories.map((category, index) => (
            <Grow in={true} timeout={300 + index * 30} key={category.id}>
              <Box>
                <Windows11CategoryButton
                  isselected={categoryFilter === category.id ? 1 : 0}
                  colorhex={category.color_hex}
                  onClick={() => handleCategoryClick(category.id)}
                  sx={{
                    whiteSpace: "nowrap",
                    minHeight: 40,
                    px: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={categoryFilter === category.id ? 600 : 500}
                  >
                    {category.title}
                  </Typography>
                </Windows11CategoryButton>
              </Box>
            </Grow>
          ))}
        </Box>

        {/* Search Input */}
        <Box
          sx={{
            p: 0,
            display: "flex",
            alignItems: "center",
            backgroundColor: grey[100],
            borderRadius: 4,
            minWidth: { xs: "100%", md: 300 },
          }}
        >
          <SearchIcon sx={{ ml: 1 }} />
          <StyledInputBase
            placeholder="Buscar Produtos…"
            inputProps={{ "aria-label": "search" }}
            value={searchQuery}
            onChange={handleProductsSearch}
          />
          {searchQuery && (
            <IconButton
              onClick={handleClear}
              aria-label="clear search"
              size="small"
            >
              <ClearIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Empty State */}
      {products.length === 0 && (
        <Box>
          <Typography variant="body1" align="center">
            Nenhum item encontrado
          </Typography>
        </Box>
      )}

      {/* Products Grid */}
      {products.length > 0 && (
        <Grid container spacing={2}>
          {products.map((product, index) => (
            <Grid
              key={product.product_id || index}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              xl={2.4}
              sx={{
                transition: "all 0.3s ease-in-out",
              }}
            >
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Products;
